/**
 * Build-time switch for developer-only surfaces that must not exist in the
 * production bundle — today `/settings/development/seeding`, which drives the
 * destructive `POST/DELETE api/dev/seed/company/{id}` pair.
 *
 * 🔴 WHY NOT THE BUNDLER'S BUILD MODE. The obvious `process.env.NODE_ENV ===
 * 'production'` check is WRONG in this repo, in both directions:
 *   1. `deploy.sh:417` runs the same `npm run build` (`webpack --mode
 *      production`) for the DEV site as for prod — the environments differ by
 *      the `.env` file copied in at `deploy.sh:415`, not by build mode. A
 *      build-mode check would delete the seeding page from mowazafdev, which is
 *      the one environment that exists to be seeded.
 *   2. `webpack.config.cjs:24` defines `process.env.NODE_ENV` from the SHELL
 *      variable (`process.env.NODE_ENV || 'development'`), which deploy.sh never
 *      sets — so it collides with the value webpack's own `mode` define emits.
 *      A flag whose value depends on which DefinePlugin instance wins is not a
 *      flag you gate a destructive surface on.
 *
 * So the axis is the per-environment `.env` file, which is exactly what it is
 * for: `environments/Development.env` sets `DEV_TOOLS=true`,
 * `environments/Production.env` sets it empty. DefinePlugin
 * (`webpack.config.cjs:17-21`) inlines the literal, so the guarded route's
 * import is statically false in the prod bundle.
 *
 * DEFAULT IS DENY. An unset/misspelled/absent key evaluates to disabled, so the
 * failure mode of a bad .env is "the dev tool is missing on dev", never "the
 * destructive tool is present on prod".
 *
 * Local `npm run dev`: put `DEV_TOOLS=true` in `mowazaf.client/.env` (untracked,
 * per-machine) — copying `environments/Development.env` over it does this for you.
 */
export const IS_DEV_TOOLS_ENABLED: boolean = readDevToolsFlag() === "true";

function readDevToolsFlag(): string | undefined {
  try {
    /*
      🔴 READ `process.env.DEV_TOOLS` DIRECTLY, AND DO NOT GUARD IT WITH `typeof process`.

      DefinePlugin substitutes exactly one thing: the literal member expression
      `process.env.DEV_TOOLS`. It does NOT substitute `typeof process`, and it does NOT
      substitute a bare `process.env`. So a guard of the shape

          if (typeof process === "undefined" || !process.env) return undefined;

      survives into the browser bundle unsubstituted, evaluates `typeof process` to
      "undefined" (webpack 5 ships no `process` shim), and returns BEFORE ever reaching the
      substituted expression -- pinning this flag to false in EVERY environment, dev included.
      That is not a safe default, it is a flag that does nothing, and it silently removes the
      seeding page from the one environment that exists to be seeded.

      The ReferenceError the guard was written to prevent is instead prevented at the source:
      `webpack.config.cjs` now defines `process.env.DEV_TOOLS` unconditionally, so this
      expression is ALWAYS replaced by a string literal at build time and no `process` object
      is ever touched at runtime. The try/catch stays as belt-and-braces for any future
      bundler that does not substitute it.

      DEFAULT IS DENY: an unset, misspelled or absent key yields "" or undefined, never "true".
    */
    return process.env.DEV_TOOLS;
  } catch {
    return undefined;
  }
}
